/**
 * SMSWiser prepared drops mutations (campaign-scoped)
 * For Media Devoted | Created By Ciro Autiero
 */

import type { ShortlinkClickIdStructure, ShortlinkDomainScheme } from '@trafficpulse/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import { smswiserKeys } from '../../smswiser/core/useSMSWiser';
import api from '@/services/api';

import { keys } from '../keys';
import { invalidatePreparedDrops } from '../invalidate-prepared-drops';
import { unwrapApiData } from '../utils';

export interface CreatePreparedDropPayload {
  workDate: string;
  titleBase?: string;
  routeId: number;
  leadsListWiserId: number;
  shortLinkTldValue: string;
  amount: number;
  batchAmount?: number;
  batchDelay?: number;
  forceSend?: boolean;
  aiOptimizationEnabled?: boolean;
  aiOptimizationType?: 'ROI' | 'CTR';
  aiOptimizationBatchAmount?: number;
  aiOptimizationMinimalValuePercent?: number;
  aiOptimizationSendUntil?: string;
  useOnlyNewDomains?: boolean;
  clickIdStructure?: ShortlinkClickIdStructure;
  domainScheme?: ShortlinkDomainScheme;
  executeAfter?: string;
  senderTemplateId?: string;
  textTemplateId?: string;
  staticTagId?: string | null;
  senderNames?: string[];
  smsTexts?: string[];
  rotators?: Record<string, string>;
}

export type UpdatePreparedDropPayload = Omit<
  Partial<CreatePreparedDropPayload>,
  'executeAfter'
> & { executeAfter?: string | null; status?: 'KILLED' };

export type BulkUpdatePreparedDropsPayload = Omit<
  Partial<CreatePreparedDropPayload>,
  | 'executeAfter'
  | 'batchAmount'
  | 'batchDelay'
  | 'aiOptimizationType'
  | 'aiOptimizationBatchAmount'
  | 'aiOptimizationMinimalValuePercent'
  | 'aiOptimizationSendUntil'
> & {
  executeAfter?: string | null;
  wiserId?: number;
  staticTagId?: string | null;
  batchAmount?: number | null;
  batchDelay?: number | null;
  aiOptimizationType?: 'ROI' | 'CTR' | null;
  aiOptimizationBatchAmount?: number | null;
  aiOptimizationMinimalValuePercent?: number | null;
  aiOptimizationSendUntil?: string | null;
  /** Client-only: expanded to per-item `smsTexts` before PATCH (merge-order rows). */
  smsTextsPerDrop?: string[][];
  senderNamesPerDrop?: string[][];
};

export function useCreatePreparedDrop(countryKey: string, wiserId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreatePreparedDropPayload) => {
      const res = await api.post(
        `/smswiser/campaigns/${countryKey}/${wiserId}/prepared-drops`,
        payload,
      );
      return unwrapApiData<PreparedDrop>(res.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.preparedDropsPrefix(countryKey, wiserId) });
      invalidatePreparedDrops(qc, countryKey);
    },
  });
}

/** Create with explicit wiserId and countryKey (copy/duplicate / multi-country lists). */
export function useCreatePreparedDropFlex() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      countryKey,
      payload,
      wiserId,
      undoGroupId,
    }: {
      countryKey: string;
      payload: CreatePreparedDropPayload;
      wiserId: number;
      /** One id per user click, so a multi-request create undoes as one step. */
      undoGroupId?: string;
    }) => {
      const res = await api.post(
        `/smswiser/campaigns/${encodeURIComponent(countryKey)}/${wiserId}/prepared-drops`,
        { ...payload, ...(undoGroupId ? { undoGroupId } : {}) },
      );
      return unwrapApiData<PreparedDrop>(res.data);
    },
    onSuccess: (_data, { countryKey, wiserId }) => {
      qc.invalidateQueries({ queryKey: keys.preparedDropsPrefix(countryKey, wiserId) });
      invalidatePreparedDrops(qc, countryKey);
      qc.invalidateQueries({ queryKey: [...smswiserKeys.country(countryKey), 'leads-usage'] });
    },
  });
}

/** Bulk-create prepared drops in one request (duplicate / copy-as-draft). */
export function useBulkCreatePreparedDrops() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      countryKey,
      wiserId,
      items,
      undoGroupId,
    }: {
      countryKey: string;
      wiserId: number;
      items: CreatePreparedDropPayload[];
      /** One id per user click — a copy over 200 items is several requests. */
      undoGroupId?: string;
    }) => {
      const res = await api.post(
        `/smswiser/campaigns/${encodeURIComponent(countryKey)}/${wiserId}/prepared-drops/bulk`,
        { items, ...(undoGroupId ? { undoGroupId } : {}) },
      );
      return unwrapApiData<{ created: number }>(res.data);
    },
    onSuccess: (_data, { countryKey, wiserId }) => {
      qc.invalidateQueries({ queryKey: keys.preparedDropsPrefix(countryKey, wiserId) });
      invalidatePreparedDrops(qc, countryKey);
      qc.invalidateQueries({ queryKey: [...smswiserKeys.country(countryKey), 'leads-usage'] });
    },
  });
}

export function useUpdatePreparedDrop(countryKey: string, wiserId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdatePreparedDropPayload }) => {
      const res = await api.patch(
        `/smswiser/campaigns/${countryKey}/${wiserId}/prepared-drops/${id}`,
        payload,
      );
      return unwrapApiData<PreparedDrop>(res.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.preparedDropsPrefix(countryKey, wiserId) });
      invalidatePreparedDrops(qc, countryKey);
      qc.invalidateQueries({ queryKey: [...smswiserKeys.country(countryKey), 'leads-usage'] });
    },
  });
}

export function useDeletePreparedDrop(countryKey: string, wiserId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/smswiser/campaigns/${countryKey}/${wiserId}/prepared-drops/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.preparedDropsPrefix(countryKey, wiserId) });
      invalidatePreparedDrops(qc, countryKey);
      qc.invalidateQueries({ queryKey: [...smswiserKeys.country(countryKey), 'leads-usage'] });
    },
  });
}

export function useSendPreparedDrop(countryKey: string, wiserId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (preparedDropId: string) => {
      const res = await api.post(
        `/smswiser/campaigns/${countryKey}/${wiserId}/prepared-drops/${preparedDropId}/create-drop`,
      );
      return unwrapApiData<{ ok: true; title: string; uniqueId: string; dropId?: number }>(res.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.preparedDropsPrefix(countryKey, wiserId) });
      invalidatePreparedDrops(qc, countryKey);
      qc.invalidateQueries({ queryKey: keys.dropsWithStatsPrefix(countryKey) });
      qc.invalidateQueries({ queryKey: [...smswiserKeys.country(countryKey), 'leads-usage'] });
    },
  });
}

export function useBulkUpdatePreparedDrops(countryKey: string, wiserId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ids,
      payload,
    }: { ids: string[]; payload: BulkUpdatePreparedDropsPayload }) => {
      const res = await api.patch(
        `/smswiser/campaigns/${countryKey}/${wiserId}/prepared-drops/bulk`,
        { ids, payload },
      );
      return unwrapApiData<{ updated: number }>(res.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.preparedDropsPrefix(countryKey, wiserId) });
      invalidatePreparedDrops(qc, countryKey);
      qc.invalidateQueries({ queryKey: [...smswiserKeys.country(countryKey), 'leads-usage'] });
    },
  });
}
